import React, { useState, useCallback } from 'react';
import { 
  Table, 
  Card, 
  Input, 
  Button, 
  Form, 
  Row, 
  Col, 
  message, 
  Tag, 
  Space,
  Typography,
  Tooltip,
  Pagination,
  Modal,
  Radio
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  StopOutlined
} from '@ant-design/icons';
import API from '../services/api';

const { Title, Text } = Typography;
const { confirm } = Modal;

const StaticBlacklistManagement = () => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [blacklistData, setBlacklistData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [hasSearched, setHasSearched] = useState(false);
  const [statistics, setStatistics] = useState(null);
  
  // 搜索条件
  const [searchForm] = Form.useForm();
  const [currentHostIp, setCurrentHostIp] = useState('');
  const [currentKeywords, setCurrentKeywords] = useState('');

  // 添加黑名单相关状态
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [addLoading, setAddLoading] = useState(false);

  // 删除相关状态
  const [deleteLoading, setDeleteLoading] = useState({});

  // 获取静态黑名单数据
  const fetchBlacklistData = useCallback(async (hostIp, keywords = '', page = 1, limit = 20) => {
    if (!hostIp) {
      message.warning('请输入主机IP地址');
      return;
    }

    try {
      setLoading(true);
      const response = await API.get('/api/admin/static-blacklist', {
        params: {
          host_ip: hostIp,
          keywords,
          page,
          limit
        }
      });

      if (response.data && response.data.code === 0) {
        const data = response.data.data;
        setBlacklistData(data.list || []);
        setTotalRecords(data.count || data.pagination?.total || 0);
        setCurrentPage(data.pagination?.page || page);
        setStatistics(data.statistics || null);
        setHasSearched(true);
        
        if (data.list && data.list.length === 0) {
          message.info('该IP没有找到静态黑名单记录');
        } else {
          message.success(`成功获取 ${data.count || data.pagination?.total || 0} 条黑名单记录`);
        }
      } else {
        message.error(response.data.msg || '获取静态黑名单失败');
        setBlacklistData([]);
        setTotalRecords(0);
        setHasSearched(true);
      }
    } catch (error) {
      console.error('获取静态黑名单失败:', error);
      let errorMsg = '获取静态黑名单失败';
      
      if (error.response && error.response.data) {
        errorMsg = error.response.data.msg || error.response.data.message || errorMsg;
      }
      
      message.error(errorMsg);
      setBlacklistData([]);
      setTotalRecords(0);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // 处理搜索
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    const { hostIp, keywords } = values;
    
    if (!hostIp || hostIp.trim() === '') {
      message.warning('请输入主机IP地址');
      return;
    }

    setCurrentHostIp(hostIp.trim());
    setCurrentKeywords(keywords ? keywords.trim() : '');
    setCurrentPage(1);
    fetchBlacklistData(hostIp.trim(), keywords ? keywords.trim() : '', 1, pageSize);
  };

  // 处理刷新
  const handleRefresh = () => {
    if (!currentHostIp) {
      message.warning('请先进行搜索');
      return;
    }
    fetchBlacklistData(currentHostIp, currentKeywords, currentPage, pageSize);
  };

  // 处理分页变化
  const handlePageChange = (page, size) => {
    if (!currentHostIp) {
      message.warning('请先进行搜索');
      return;
    }
    setCurrentPage(page);
    setPageSize(size);
    fetchBlacklistData(currentHostIp, currentKeywords, page, size);
  };

  // 格式化IP地址
  const formatIP = (ip) => {
    if (!ip) return '-';
    return (
      <Tag color="red" style={{ fontFamily: 'monospace' }}>
        {ip}
      </Tag>
    );
  };

  // 显示添加黑名单模态框
  const showAddModal = () => {
    if (!currentHostIp) {
      message.warning('请先查询主机IP');
      return;
    }
    addForm.resetFields();
    setAddModalVisible(true);
  };

  // 添加黑名单
  const handleAddBlacklist = async () => {
    try {
      const values = await addForm.validateFields();
      setAddLoading(true);

      // 构建请求数据
      const requestData = {
        type: values.type,
        start_ip: values.start_ip,
        end_ip: values.type === 1 ? values.end_ip : '',
        target_ips: [currentHostIp],
        remark: values.remark || ''
      };

      const response = await API.post('/api/admin/static-blacklist', requestData);

      if (response.data && response.data.code === 0) {
        message.success('添加静态黑名单成功');
        setAddModalVisible(false);
        addForm.resetFields();
        // 刷新数据
        handleRefresh();
      } else {
        message.error(response.data.msg || '添加静态黑名单失败');
      }
    } catch (error) {
      console.error('添加静态黑名单失败:', error);
      let errorMsg = '添加静态黑名单失败';
      
      if (error.response && error.response.data) {
        errorMsg = error.response.data.msg || error.response.data.message || errorMsg;
      }
      
      message.error(errorMsg);
    } finally {
      setAddLoading(false);
    }
  };

  // 删除黑名单
  const handleDeleteBlacklist = (record) => {
    const { id, ip_range, remark } = record;
    
    confirm({
      title: '确认删除黑名单',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定要删除这条黑名单记录吗？</p>
          <div style={{ padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', margin: '8px 0' }}>
            <Text strong>IP地址: </Text><Text code>{ip_range}</Text><br />
            {remark && (
              <>
                <Text strong>备注: </Text><Text>{remark}</Text>
              </>
            )}
          </div>
          <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
            此操作不可恢复，请谨慎操作！
          </p>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          setDeleteLoading(prev => ({ ...prev, [id]: true }));
          
          const response = await API.delete(`/api/admin/static-blacklist/${id}`);
          
          if (response.data && response.data.code === 0) {
            message.success('删除静态黑名单成功');
            // 刷新数据
            handleRefresh();
          } else {
            message.error(response.data.msg || '删除静态黑名单失败');
          }
        } catch (error) {
          console.error('删除静态黑名单失败:', error);
          let errorMsg = '删除静态黑名单失败';
          
          if (error.response && error.response.data) {
            errorMsg = error.response.data.msg || error.response.data.message || errorMsg;
          }
          
          message.error(errorMsg);
        } finally {
          setDeleteLoading(prev => ({ ...prev, [id]: false }));
        }
      }
    });
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => id || '-'
    },
    {
      title: '拦截IP',
      dataIndex: 'ip_range',
      key: 'ip_range',
      width: 160,
      render: formatIP
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 120,
      render: (remark) => (
        remark ? (
          <Tag color="orange">{remark}</Tag>
        ) : (
          <Text type="secondary">无备注</Text>
        )
      )
    },
    {
      title: '创建时间',
      dataIndex: 'create_time_formatted',
      key: 'create_time_formatted',
      width: 180,
      render: (time) => time || '-'
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: () => (
        <Tag color="red" icon={<StopOutlined />}>
          已生效
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Tooltip title="删除黑名单记录">
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={deleteLoading[record.id]}
            onClick={() => handleDeleteBlacklist(record)}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>静态黑名单管理</Title>
        <Text type="secondary">
          查看和管理系统静态黑名单，管理需要拦截的恶意IP地址和网络流量
        </Text>
      </div>

      {/* 搜索条件卡片 */}
      <Card title="查询条件" style={{ marginBottom: '16px' }}>
        <Form 
          form={searchForm}
          layout="vertical"
          onFinish={handleSearch}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="hostIp"
                label="主机IP地址"
                rules={[
                  { required: true, message: '请输入主机IP地址' },
                  {
                    pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                    message: '请输入正确的IP地址格式'
                  }
                ]}
              >
                <Input 
                  placeholder="请输入要查询的主机IP地址"
                  suffix={
                    <Tooltip title="输入要查询黑名单的主机IP地址">
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </Tooltip>
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="keywords"
                label="关键词"
              >
                <Input 
                  placeholder="可选，输入搜索关键词"
                  suffix={
                    <Tooltip title="根据IP地址或备注内容进行模糊搜索">
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </Tooltip>
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label=" " colon={false}>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />} 
                    htmlType="submit"
                    loading={loading}
                  >
                    查询
                  </Button>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={handleRefresh}
                    loading={loading}
                    disabled={!hasSearched}
                  >
                    刷新
                  </Button>
                  <Button 
                    type="default"
                    icon={<PlusOutlined />} 
                    onClick={showAddModal}
                    disabled={!hasSearched}
                  >
                    添加黑名单
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 统计信息 */}
      {hasSearched && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Text>查询主机: </Text>
                <Tag color="blue">{currentHostIp}</Tag>
                {currentKeywords && (
                  <>
                    <Text>关键词: </Text>
                    <Tag color="purple">{currentKeywords}</Tag>
                  </>
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Text strong>
                  共找到 {totalRecords} 条黑名单记录
                </Text>
                {statistics && (
                  <Space>
                    <Tag color="red">
                      黑名单条目: {statistics.total_blacklist_entries || totalRecords}
                    </Tag>
                    <Tag color="volcano">
                      单个IP: {statistics.types?.single_ip || 0}
                    </Tag>
                    <Tag color="magenta">
                      IP范围: {statistics.types?.ip_range || 0}
                    </Tag>
                  </Space>
                )}
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* 数据表格 */}
      <Card title="黑名单记录" size="small">
        <Table
          columns={columns}
          dataSource={blacklistData}
          rowKey={(record, index) => record.id || `${record.ip_range}_${index}`}
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 1200 }}
          locale={{
            emptyText: hasSearched ? '暂无黑名单记录' : '请输入查询条件进行搜索'
          }}
        />
        
        {/* 自定义分页 */}
        {hasSearched && totalRecords > 0 && (
          <div style={{ marginTop: '16px', textAlign: 'right' }}>
            <Pagination
              current={currentPage}
              total={totalRecords}
              pageSize={pageSize}
              showTotal={(total, range) => 
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
              }
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      {/* 添加黑名单模态框 */}
      <Modal
        title="添加静态黑名单"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
        }}
        onOk={handleAddBlacklist}
        okText="确认添加"
        cancelText="取消"
        confirmLoading={addLoading}
        width={600}
        centered
      >
        <Form
          form={addForm}
          layout="vertical"
          initialValues={{
            type: 0,
            remark: ''
          }}
        >
          <Form.Item
            name="type"
            label="IP类型"
            rules={[{ required: true, message: '请选择IP类型' }]}
          >
            <Radio.Group>
              <Radio value={0}>单个IP</Radio>
              <Radio value={1}>IP段</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="start_ip"
            label="起始IP地址"
            rules={[
              { required: true, message: '请输入起始IP地址' },
              {
                pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                message: '请输入正确的IP地址格式'
              }
            ]}
          >
            <Input placeholder="请输入IP地址，如: 192.168.1.1" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
          >
            {({ getFieldValue }) => {
              return getFieldValue('type') === 1 ? (
                <Form.Item
                  name="end_ip"
                  label="结束IP地址"
                  rules={[
                    { required: true, message: '请输入结束IP地址' },
                    {
                      pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                      message: '请输入正确的IP地址格式'
                    }
                  ]}
                >
                  <Input placeholder="请输入结束IP地址，如: 192.168.1.100" />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          <Form.Item
            name="remark"
            label="备注"
          >
            <Input.TextArea 
              rows={3}
              placeholder="请输入备注信息（可选）"
              maxLength={200}
              showCount
            />
          </Form.Item>

          <div style={{ padding: '12px 16px', backgroundColor: '#fff2f0', borderRadius: '6px', marginBottom: '16px', border: '1px solid #ffccc7' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <strong>提示：</strong>
              <br />
              • 单个IP：只需填写起始IP地址
              <br />
              • IP段：需要填写起始和结束IP地址
              <br />
              • 黑名单将添加到当前查询的主机IP：{currentHostIp}
              <br />
              • <Text type="danger">添加到黑名单的IP将被系统拦截，请谨慎操作！</Text>
            </Text>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default StaticBlacklistManagement; 